import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { debounceTime, EMPTY, filter, of } from 'rxjs';
import { notificationsActions } from '../actions/notifications.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { StoreState } from '../store.state';
import { userActions } from '@store/actions/user.actions';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { NotificationCountersMapper } from '@core/domain/mappers/notifications/notification-counters.mapper';
import { NotificationsMapper } from '@core/domain/mappers/notifications/notifications.mapper';
import { ApiNotificationsService } from '@core/api/services/notifications/api-notifications.service';
import { notificationsSelectors } from '@store/selectors/notifications.selectors';

@Injectable()
export class NotificationsEffects {
  initialLoad$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.setUser),
      map(() => notificationsActions.fetchCounters())
    )
  );

  fetchNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.setCategory, notificationsActions.fetchAll),
      switchMap((action) =>
        action.type === notificationsActions.setCategory.type
          ? of(action.category)
          : this._store.select(notificationsSelectors.currentCategory)
      ),
      debounceTime(32),
      switchMap((category) =>
        this._notificationsService.fetch$(NotificationsMapper.categoryToPath(category)).pipe(
          map((api) => api.data),
          map(NotificationsMapper.fromApiToModel),
          map((notifications) => notificationsActions.setNotifications({ notifications })),
          catchError((err: HttpErrorResponse) => of(notificationsActions.errorNotifications({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    )
  );

  fetchCounters$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.fetchCounters, notificationsActions.fetchAll),
      switchMap(() =>
        this._notificationsService.fetchCounters$().pipe(
          map((api) => api.data),
          map(NotificationCountersMapper.fromApiToModel),
          map((counters) => notificationsActions.setCounters({ counters })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  readAll$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.readAll),
      exhaustMap(() => this._notificationsService.markAllAsRead$().pipe(catchError(() => EMPTY))),
      filter((response) => response.data),
      map(() => notificationsActions.setReadAll())
    )
  );

  readOne$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.readOne),
      mergeMap(({ notificationId }) =>
        this._notificationsService.markOneAsRead$(notificationId).pipe(
          filter((response) => response.data),
          map(() => notificationsActions.setReadOne({ notificationId })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  setReadOne$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.setReadOne),
      debounceTime(1000),
      map(() => notificationsActions.fetchCounters())
    )
  );

  archiveAll$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.archiveAll),
      exhaustMap(() => this._notificationsService.archiveAll$().pipe(catchError(() => EMPTY))),
      filter((response) => response.data),
      map(() => notificationsActions.fetchAll())
    )
  );

  archiveOne$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.archiveOne),
      mergeMap(({ notificationId }) => this._notificationsService.archive$(notificationId).pipe(catchError(() => EMPTY))),
      debounceTime(500),
      filter((response) => response.data),
      map(() => notificationsActions.fetchAll())
    )
  );

  unArchiveOne$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notificationsActions.unArchiveOne),
      mergeMap(({ notificationId }) => this._notificationsService.unarchive$(notificationId).pipe(catchError(() => EMPTY))),
      debounceTime(500),
      filter((response) => response.data),
      map(() => notificationsActions.fetchAll())
    )
  );

  constructor(
    private _actions$: Actions,
    private _notificationsService: ApiNotificationsService,
    private _store: Store<StoreState>
  ) {}
}
